import { PersonProfile, usePersonProfiles } from '@/hooks/usePersonProfiles';
import { Badge } from '@/components/ui/badge';
import { calculateAge } from '@/lib/utils';
import { useModalNavigation } from '@/hooks/useModalNavigation';
import { useFamily } from '@/hooks/useFamily';
import { useToast } from '@/hooks/use-toast';
import { usePersonUpcomingItems } from '@/hooks/usePersonUpcomingItems';
import { Users, Heart, UserPlus, Stethoscope, Link, GraduationCap, Trophy, Briefcase, UserCircle, PawPrint } from 'lucide-react';

interface VisualPersonCardProps {
  person: PersonProfile;
}

const categoryGradients = {
  family: 'from-blue-500 to-purple-600',
  extended_family: 'from-orange-500 to-pink-600',
  friends: 'from-green-500 to-blue-600',
  medical_other: 'from-red-500 to-orange-600',
  school: 'from-indigo-500 to-blue-600',
  sport: 'from-emerald-500 to-teal-600',
  services: 'from-purple-500 to-pink-600',
  professional: 'from-slate-500 to-gray-600',
  pets: 'from-amber-500 to-yellow-600'
};

const categoryIcons = {
  family: Users,
  extended_family: Heart,
  friends: UserPlus,
  medical_other: Stethoscope,
  school: GraduationCap,
  sport: Trophy,
  services: Briefcase,
  professional: UserCircle,
  pets: PawPrint
};

export const VisualPersonCard = ({ person }: VisualPersonCardProps) => {
  const fullName = `${person.first_name} ${person.last_name}`;
  const age = person.date_of_birth ? calculateAge(person.date_of_birth) : null;
  const { openModal } = useModalNavigation();
  const { family } = useFamily();
  const { createPerson, refreshPersons, persons } = usePersonProfiles(family?.id);
  const { toast } = useToast();
  const CategoryIcon = categoryIcons[person.category];
  const { upcomingCount, hasUpcomingItems } = usePersonUpcomingItems(person.id, person.profile_id);

  const handleCardClick = async () => {
    // Check if this is an artificial profile (user's own profile)
    if (person.id.startsWith('profile-') && person.profile_id) {
      try {
        // Create a real person_profiles entry for the user
        const personData = {
          first_name: person.first_name,
          last_name: person.last_name,
          category: person.category,
          family_role: person.family_role,
          date_of_birth: person.date_of_birth || '',
          gender: person.gender || undefined,
          phone_primary: person.phone_primary || '',
          email: person.email || '',
          address: person.address || '',
          notes: person.notes || '',
          emergency_contact: person.emergency_contact || false,
          profile_id: person.profile_id
        };

        await createPerson(personData);
        
        // Refresh the data to get the new person
        await refreshPersons();
        
        // Find the created person by profile_id and open view modal
        setTimeout(() => {
          const createdPerson = persons.find(p => p.profile_id === person.profile_id && !p.id.startsWith('profile-'));
          if (createdPerson) {
            openModal('viewPerson', { id: createdPerson.id });
          }
        }, 100); // Small delay to ensure data is refreshed
        
      } catch (error) {
        console.error('Error creating person profile:', error);
        toast({
          title: "Erreur",
          description: "Impossible de créer votre profil. Veuillez réessayer.",
          variant: "destructive",
        });
      }
    } else {
      // Normal person, open view modal directly
      openModal('viewPerson', { id: person.id });
    }
  };

  const displayName = person.category === 'family' ? person.first_name : fullName;

  return (
    <div 
      className="group relative aspect-[4/5] overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer"
      onClick={handleCardClick}
    >
      {/* Background Image or Gradient */}
      <div className="absolute inset-0">
        {person.avatar_url ? (
          <img 
            src={person.avatar_url} 
            alt={fullName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${categoryGradients[person.category]} flex items-center justify-center`}>
            <CategoryIcon className="w-16 h-16 text-white/40" />
          </div>
        )}
      </div>

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

      {/* Emergency Badge - Top Left */}
      {person.emergency_contact && (
        <div className="absolute top-2 left-2">
          <Badge className="bg-red-600 text-white border-none text-xs px-2">
            🚨 Urgence
          </Badge>
        </div>
      )}

      {/* Notification Badge - Top Right */}
      {hasUpcomingItems && (
        <div className="absolute top-2 right-2 z-10">
          <Badge 
            className="h-4 w-4 p-0 flex items-center justify-center text-white text-xs font-medium rounded-full border-0"
            style={{ backgroundColor: '#07b84a' }}
          >
            {upcomingCount > 9 ? '9+' : upcomingCount}
          </Badge>
        </div>
      )}

      {/* Linked Account Badge - Top Right (below notification if both present) */}
      {person.profile_id && (
        <div className={`absolute ${hasUpcomingItems ? 'top-8' : 'top-2'} right-2 z-10`}>
          <Badge 
            className="h-6 w-6 p-0 flex items-center justify-center text-white rounded-full border-2 border-white shadow-md"
            style={{ backgroundColor: '#07b84a' }}
          >
            <Link className="h-3 w-3" />
          </Badge>
        </div>
      )}

      {/* Content - Bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <div className="text-white">
          <h3 className="font-semibold text-lg leading-tight mb-1">
            {displayName}
          </h3>
          
          {/* Age for family members */}
          {person.category === 'family' && age && (
            <p className="text-white/80 text-sm mb-2">
              {age} ans
            </p>
          )}
        </div>
      </div>
    </div>
  );
};